import { useState, useEffect } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { 
  Search, 
  CreditCard,
  ExternalLink,
  RefreshCw,
  Loader2,
  CheckCircle,
  XCircle,
  AlertCircle,
  Clock,
  Unlink,
  Store,
  Copy,
  Check,
  Eye,
  Zap,
  LinkIcon,
} from 'lucide-react';
import AdminLayout from './AdminLayout';
import { apiRequest, queryClient } from '@/lib/queryClient';
import { useToast } from '@/hooks/use-toast';

interface StripeRestaurant {
  id: number;
  name: string;
  address: string;
  city?: string;
  state?: string;
  status?: string;
  ready?: boolean;
  phone?: string;
  domain?: string;
  stripe_account: string | null;
  stripe_fee_ratio: string | null;
  live_website?: string;
}

interface StripeAccountDetails {
  id: string;
  charges_enabled: boolean;
  payouts_enabled: boolean;
  details_submitted: boolean;
  capabilities: Record<string, string>;
  business_type: string | null;
  country: string | null;
  email: string | null;
  created: number;
}

interface StatusResponse {
  restaurant: { id: number; name: string; stripe_account: string | null };
  stripeAccount: StripeAccountDetails | null;
  status: string;
  error?: string;
}

function getStatusBadge(stripeAccount: string | null, detailedStatus?: string) {
  if (detailedStatus === 'active') {
    return <Badge variant="default" className="bg-green-600"><CheckCircle className="w-3 h-3 mr-1" />Active</Badge>;
  }
  if (detailedStatus === 'pending_verification') {
    return <Badge variant="default" className="bg-yellow-600"><Clock className="w-3 h-3 mr-1" />Pending</Badge>;
  }
  if (detailedStatus === 'onboarding_incomplete') {
    return <Badge variant="default" className="bg-orange-600"><AlertCircle className="w-3 h-3 mr-1" />Incomplete</Badge>;
  }
  if (stripeAccount) {
    return <Badge variant="default" className="bg-blue-600"><LinkIcon className="w-3 h-3 mr-1" />Connected</Badge>;
  }
  return <Badge variant="secondary"><XCircle className="w-3 h-3 mr-1" />Not Connected</Badge>;
}

export default function AdminStripeConnect() {
  const { toast } = useToast();
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [showDetailsDialog, setShowDetailsDialog] = useState(false);
  const [detailsData, setDetailsData] = useState<StatusResponse | null>(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [restaurantStatuses, setRestaurantStatuses] = useState<Record<number, string>>({});

  const { data: restaurants = [], isLoading, refetch } = useQuery<StripeRestaurant[]>({
    queryKey: ['/api/admin/stripe-connect/restaurants'],
  });

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const connectedId = params.get('connected');
    const accountId = params.get('account');
    const error = params.get('error');

    if (connectedId && accountId) {
      toast({
        title: 'Stripe Account Connected',
        description: `Restaurant ${connectedId} connected to Stripe account ${accountId}`,
      });
      refetch();
      window.history.replaceState({}, '', '/admin/stripe-connect');
    } else if (error) {
      const errorMessages: Record<string, string> = {
        oauth_denied: 'The restaurant owner denied the Stripe authorization.',
        oauth_failed: 'Failed to complete the Stripe OAuth flow. Please try again.',
        missing_params: 'Missing OAuth parameters. Please try again.',
        invalid_state: 'Invalid OAuth state. Please try again.',
      };
      toast({
        title: 'Connection Failed',
        description: errorMessages[error] || `OAuth error: ${error}`,
        variant: 'destructive',
      });
      window.history.replaceState({}, '', '/admin/stripe-connect');
    }
  }, []);

  const connectMutation = useMutation({
    mutationFn: async (restaurantId: number) => {
      const response = await apiRequest('POST', '/api/admin/stripe-connect/oauth-url', { restaurantId });
      return response.json();
    },
    onSuccess: (data) => {
      window.location.href = data.url;
    },
    onError: (error: any) => {
      toast({ title: 'Error', description: error.message || 'Failed to generate connect URL', variant: 'destructive' });
    },
  });

  const disconnectMutation = useMutation({
    mutationFn: async (restaurantId: number) => {
      const response = await apiRequest('POST', '/api/admin/stripe-connect/disconnect', { restaurantId });
      return response.json();
    },
    onSuccess: () => {
      toast({ title: 'Disconnected', description: 'Stripe account has been disconnected from this restaurant.' });
      queryClient.invalidateQueries({ queryKey: ['/api/admin/stripe-connect/restaurants'] });
      setShowDetailsDialog(false);
      setDetailsData(null);
    },
    onError: (error: any) => {
      toast({ title: 'Error', description: error.message || 'Failed to disconnect', variant: 'destructive' });
    },
  });

  const handleCheckStatus = async (restaurantId: number) => {
    setDetailsLoading(true);
    try {
      const response = await apiRequest('GET', `/api/admin/stripe-connect/status/${restaurantId}`);
      const data: StatusResponse = await response.json();
      setDetailsData(data);
      setShowDetailsDialog(true);
      if (data.status) {
        setRestaurantStatuses(prev => ({ ...prev, [restaurantId]: data.status }));
      }
    } catch (error: any) {
      toast({ title: 'Error', description: error.message || 'Failed to check status', variant: 'destructive' });
    } finally {
      setDetailsLoading(false);
    }
  };

  const handleCopyId = (id: string) => {
    navigator.clipboard.writeText(id);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const getEffectiveStatus = (r: StripeRestaurant): string | null => {
    if (restaurantStatuses[r.id]) return restaurantStatuses[r.id];
    if (r.stripe_account) return 'connected';
    return 'not_connected';
  };

  const filteredRestaurants = restaurants.filter((r) => {
    const matchesSearch = r.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      r.address?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      r.stripe_account?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      r.domain?.toLowerCase().includes(searchQuery.toLowerCase());

    const status = getEffectiveStatus(r);
    if (statusFilter === 'all') return matchesSearch;
    if (statusFilter === 'connected') return matchesSearch && r.stripe_account;
    if (statusFilter === 'not_connected') return matchesSearch && !r.stripe_account;
    if (statusFilter === 'active') return matchesSearch && status === 'active';
    if (statusFilter === 'incomplete') return matchesSearch && r.stripe_account && status !== 'active' && status !== 'connected';
    return matchesSearch;
  });

  const stats = {
    total: restaurants.length,
    connected: restaurants.filter(r => r.stripe_account).length,
    active: restaurants.filter(r => restaurantStatuses[r.id] === 'active').length,
    notConnected: restaurants.filter(r => !r.stripe_account).length,
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <CreditCard className="w-6 h-6" />
              Stripe Connect
            </h1>
            <p className="text-muted-foreground text-sm mt-1">
              Manage Stripe Connect accounts for Eatzy restaurant payment processing
            </p>
          </div>
          <Button onClick={() => refetch()} variant="outline" size="sm" data-testid="button-refresh-stripe">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card className="cursor-pointer" onClick={() => setStatusFilter('all')} data-testid="stat-total">
            <CardContent className="p-4">
              <p className="text-xs text-muted-foreground">Total Restaurants</p>
              <p className="text-2xl font-bold">{stats.total}</p>
            </CardContent>
          </Card>
          <Card className="cursor-pointer" onClick={() => setStatusFilter('connected')} data-testid="stat-connected">
            <CardContent className="p-4">
              <p className="text-xs text-muted-foreground flex items-center gap-1"><LinkIcon className="w-3 h-3 text-blue-500" />Connected</p>
              <p className="text-2xl font-bold text-blue-600">{stats.connected}</p>
            </CardContent>
          </Card>
          <Card className="cursor-pointer" onClick={() => setStatusFilter('active')} data-testid="stat-active">
            <CardContent className="p-4">
              <p className="text-xs text-muted-foreground flex items-center gap-1"><CheckCircle className="w-3 h-3 text-green-500" />Active</p>
              <p className="text-2xl font-bold text-green-600">{stats.active}</p>
            </CardContent>
          </Card>
          <Card className="cursor-pointer" onClick={() => setStatusFilter('not_connected')} data-testid="stat-not-connected">
            <CardContent className="p-4">
              <p className="text-xs text-muted-foreground flex items-center gap-1"><XCircle className="w-3 h-3 text-muted-foreground" />Not Connected</p>
              <p className="text-2xl font-bold">{stats.notConnected}</p>
            </CardContent>
          </Card>
        </div>

        <div className="flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search restaurants, addresses, or Stripe IDs..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
              data-testid="input-search-stripe"
            />
          </div>
          <div className="flex items-center gap-1">
            {['all', 'connected', 'active', 'not_connected'].map((filter) => (
              <Button
                key={filter}
                variant={statusFilter === filter ? 'default' : 'outline'}
                size="sm"
                onClick={() => setStatusFilter(filter)}
                data-testid={`filter-${filter}`}
              >
                {filter === 'all' ? 'All' : filter === 'not_connected' ? 'Not Connected' : filter.charAt(0).toUpperCase() + filter.slice(1)}
              </Button>
            ))}
          </div>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
          </div>
        ) : filteredRestaurants.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12">
              <Store className="w-12 h-12 text-muted-foreground mb-3" />
              <p className="text-muted-foreground">No restaurants found matching your criteria</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-2">
            {filteredRestaurants.map((restaurant) => {
              const effectiveStatus = getEffectiveStatus(restaurant);
              return (
                <Card key={restaurant.id} data-testid={`restaurant-row-${restaurant.id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                      <div className="flex items-center gap-3 min-w-0 flex-1">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-medium truncate">{restaurant.name}</span>
                            {restaurant.ready && (
                              <Badge variant="secondary" className="text-xs"><Zap className="w-3 h-3 mr-0.5" />Ready</Badge>
                            )}
                            {restaurant.domain && (
                              <span className="text-xs text-muted-foreground">{restaurant.domain}</span>
                            )}
                          </div>
                          <p className="text-xs text-muted-foreground mt-0.5">
                            {restaurant.address}
                            {restaurant.stripe_account && (
                              <span className="ml-2 font-mono">
                                {restaurant.stripe_account}
                                <button 
                                  onClick={() => handleCopyId(restaurant.stripe_account!)}
                                  className="ml-1 inline-flex"
                                >
                                  {copiedId === restaurant.stripe_account ? (
                                    <Check className="w-3 h-3 text-green-500" />
                                  ) : (
                                    <Copy className="w-3 h-3 text-muted-foreground" />
                                  )}
                                </button>
                              </span>
                            )}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center gap-2 flex-shrink-0">
                        {getStatusBadge(restaurant.stripe_account, effectiveStatus !== 'connected' ? effectiveStatus || undefined : undefined)}

                        {!restaurant.stripe_account ? (
                          <Button
                            size="sm"
                            onClick={() => connectMutation.mutate(restaurant.id)}
                            disabled={connectMutation.isPending}
                            data-testid={`button-connect-${restaurant.id}`}
                          >
                            {connectMutation.isPending ? (
                              <Loader2 className="w-4 h-4 animate-spin mr-1" />
                            ) : (
                              <ExternalLink className="w-4 h-4 mr-1" />
                            )}
                            Connect Stripe
                          </Button>
                        ) : (
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={() => handleCheckStatus(restaurant.id)}
                            disabled={detailsLoading}
                            data-testid={`button-details-${restaurant.id}`}
                          >
                            {detailsLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4" />}
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        <Dialog open={showDetailsDialog} onOpenChange={setShowDetailsDialog}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <CreditCard className="w-5 h-5" />
                Stripe Account Details
              </DialogTitle>
            </DialogHeader>
            {detailsLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin" />
              </div>
            ) : detailsData ? (
              <div className="space-y-4">
                <div className="p-3 rounded-md bg-muted">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div>
                      <p className="font-medium">{detailsData.restaurant.name}</p>
                      <p className="text-xs text-muted-foreground font-mono">{detailsData.restaurant.stripe_account}</p>
                    </div>
                    {getStatusBadge(detailsData.restaurant.stripe_account, detailsData.status)}
                  </div>
                </div>

                {detailsData.stripeAccount ? (
                  <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div className="p-3 rounded-md border">
                        <p className="text-xs text-muted-foreground">Charges</p>
                        <div className="flex items-center gap-1 mt-1">
                          {detailsData.stripeAccount.charges_enabled ? (
                            <><CheckCircle className="w-4 h-4 text-green-500" /><span className="text-sm font-medium">Enabled</span></>
                          ) : (
                            <><XCircle className="w-4 h-4 text-red-500" /><span className="text-sm font-medium">Disabled</span></>
                          )}
                        </div>
                      </div>
                      <div className="p-3 rounded-md border">
                        <p className="text-xs text-muted-foreground">Payouts</p>
                        <div className="flex items-center gap-1 mt-1">
                          {detailsData.stripeAccount.payouts_enabled ? (
                            <><CheckCircle className="w-4 h-4 text-green-500" /><span className="text-sm font-medium">Enabled</span></>
                          ) : (
                            <><XCircle className="w-4 h-4 text-red-500" /><span className="text-sm font-medium">Disabled</span></>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div className="p-3 rounded-md border">
                        <p className="text-xs text-muted-foreground">Details Submitted</p>
                        <p className="text-sm font-medium mt-1">{detailsData.stripeAccount.details_submitted ? 'Yes' : 'No'}</p>
                      </div>
                      <div className="p-3 rounded-md border">
                        <p className="text-xs text-muted-foreground">Country</p>
                        <p className="text-sm font-medium mt-1">{detailsData.stripeAccount.country || 'N/A'}</p>
                      </div>
                    </div>

                    {detailsData.stripeAccount.capabilities && Object.keys(detailsData.stripeAccount.capabilities).length > 0 && (
                      <div className="p-3 rounded-md border">
                        <p className="text-xs text-muted-foreground mb-2">Capabilities</p>
                        <div className="flex flex-wrap gap-1">
                          {Object.entries(detailsData.stripeAccount.capabilities).map(([cap, status]) => (
                            <Badge key={cap} variant={status === 'active' ? 'default' : 'secondary'} className={status === 'active' ? 'bg-green-600' : ''}>
                              {cap}: {status}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}

                    {detailsData.stripeAccount.email && (
                      <div className="p-3 rounded-md border">
                        <p className="text-xs text-muted-foreground">Email</p>
                        <p className="text-sm font-medium mt-1">{detailsData.stripeAccount.email}</p>
                      </div>
                    )}

                    {detailsData.stripeAccount.created && (
                      <div className="p-3 rounded-md border">
                        <p className="text-xs text-muted-foreground">Created</p>
                        <p className="text-sm font-medium mt-1">{new Date(detailsData.stripeAccount.created * 1000).toLocaleDateString()}</p>
                      </div>
                    )}
                  </div>
                ) : detailsData.error ? (
                  <div className="p-3 rounded-md bg-destructive/10 text-destructive text-sm">
                    <AlertCircle className="w-4 h-4 inline mr-1" />
                    Could not retrieve Stripe account details: {detailsData.error}
                  </div>
                ) : (
                  <p className="text-muted-foreground text-center py-4">No Stripe account connected</p>
                )}
              </div>
            ) : null}
            <DialogFooter>
              {detailsData?.restaurant?.stripe_account && (
                <Button
                  variant="destructive"
                  onClick={() => disconnectMutation.mutate(detailsData.restaurant.id)}
                  disabled={disconnectMutation.isPending}
                  data-testid="button-disconnect"
                >
                  {disconnectMutation.isPending ? (
                    <><Loader2 className="w-4 h-4 animate-spin mr-2" />Disconnecting...</>
                  ) : (
                    <><Unlink className="w-4 h-4 mr-2" />Disconnect</>
                  )}
                </Button>
              )}
              <Button variant="outline" onClick={() => setShowDetailsDialog(false)}>Close</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
